import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'network-ipam',
  slug: 'network-ipam',
  name: 'Network & IPAM',
  description: 'Per-tenant network documentation and read-only monitoring across FortiGate/Omada/Meraki/UniFi/an on-prem agent, guarded config and firmware push to network gear, and lightweight in-app IP address management.',
  icon: 'Router',
  endpoints: [
    {
      slug: 'config-get',
      method: 'GET',
      path: '/api/network/config',
      summary: 'Get network monitoring configuration',
      description: 'Returns which network provider is active for this tenant (FortiGate, Omada, Meraki, UniFi, or an on-prem polling agent), the section list each supports, and each provider\'s non-secret connection fields (host, whether a token/key is set, TLS verification). A site can run more than one provider at once.',
      auth: 'session',
      responseExample: `{
  "provider": "fortigate",
  "providers": [
    { "id": "fortigate", "name": "FortiGate (FortiOS)", "sections": ["status", "interfaces", "dhcp", "firewall", "routes", "wlan", "clients"], "configured": true },
    { "id": "omada", "name": "TP-Link Omada", "sections": ["networks", "clients"], "configured": false }
  ],
  "fortigate": { "host": "10.20.0.1", "vdom": "root", "verifyTls": true, "hasToken": true, "configured": true },
  "agent": { "configured": false, "ageSec": null, "stale": false }
}`,
      sourceFile: 'routes/network.js',
    },
    {
      slug: 'deploy-preview',
      method: 'GET',
      path: '/api/network/deploy/preview',
      summary: 'Preview a documented-network deployment',
      description: 'Builds a dry-run deployment plan (phased tasks plus a full provisioning script) from whatever this tenant\'s Network module has documented — VLANs, DHCP scopes, SSIDs, DNS forwarders, APs, switches, and gateways — without pushing or writing anything. Pair with POST /api/network/deploy/build to turn the same plan into a tracked onboarding project with per-phase tickets.',
      auth: 'session',
      params: [
        { name: 'target', in: 'query', type: 'string', description: 'Deployment target: fortigate or omada.' },
        { name: 'dhcpTarget', in: 'query', type: 'string', description: '"keep" (leave DHCP where it is) or "firewall" (migrate DHCP onto the firewall).' },
      ],
      responseExample: `{
  "summary": { "vlans": 6, "dhcpScopes": 4, "ssids": 3, "dnsForwarders": 2, "aps": 11, "switches": 3, "gateways": 1 },
  "phases": [
    { "title": "Phase 1 — VLANs & interfaces", "priority": "High", "detail": "Create 6 VLAN interfaces on the FortiGate to match the documented network." }
  ]
}`,
      sourceFile: 'routes/network.js, lib/netdeploy.js',
    },
    {
      slug: 'health',
      method: 'GET',
      path: '/api/network/health',
      summary: 'Get network health snapshot',
      description: 'Compact, cheap health rollup for the dashboard card: down interfaces, offline or low-toner printers, low-space disks, stopped services, expiring certificates, stale AD accounts, unreachable SNMP devices, and UPS-on-battery flags — read from the active provider\'s last stored snapshot, never a live device call, so it\'s safe to hit on every dashboard load.',
      auth: 'session',
      responseExample: `{
  "provider": "agent",
  "configured": true,
  "fresh": { "ageSec": 95, "stale": false, "deviceLabel": "St. Edward Server Agent" },
  "stats": { "interfacesUp": 22, "interfacesDown": 1, "clients": 184, "printers": 6 },
  "flags": [
    { "sev": "crit", "text": "1 interface down" },
    { "sev": "warn", "text": "1 printer low/out of toner" }
  ],
  "status": "critical"
}`,
      sourceFile: 'routes/network.js',
    },
    {
      slug: 'discovery-results',
      method: 'GET',
      path: '/api/network/discovery',
      summary: 'List SNMP subnet discovery scans',
      description: 'Returns the most recent SNMP-based subnet discovery jobs and their results — devices found responding on a scanned CIDR that aren\'t already in the continuous poll list. Review-only: a discovered device is never auto-added to monitoring, an admin copies the ones they want into the agent config by hand. POST to the same path to queue a new scan.',
      auth: 'session',
      responseExample: `[
  {
    "id": "ndj_3a90",
    "subnetCidr": "10.20.30.0/24",
    "status": "completed",
    "completedAt": "2026-07-21T12:04:11.000Z",
    "found": [
      { "ip": "10.20.30.14", "sysName": "sw-lib-01", "sysDescr": "TP-Link JetStream", "reachable": true }
    ]
  }
]`,
      sourceFile: 'routes/network.js, lib/netdiscover.js',
    },
    {
      slug: 'network-push-issue',
      method: 'POST',
      path: '/api/network-push/issue',
      summary: 'Issue a network config or firmware push',
      description: 'Issues a guarded config or firmware change to a FortiGate or Omada device that already appears in that vendor\'s own reported inventory (device-of-record binding — a free-text host or IP is never accepted). Every issued push takes a mandatory pre-push Config Archive snapshot first, so a failed or unreachable-after-push change can be reverted from that snapshot. This is a direct, SSRF-guarded server-to-vendor-API call, not a signed job dispatched to an endpoint agent — the Ed25519 signed-job model used for RMM endpoint capabilities doesn\'t apply here. Depending on whether an armed auto-approval policy fully covers the job, the response is either applied immediately or left pending-approval for POST /api/network-push/jobs/:id/approve.',
      auth: 'session',
      requestBodyExample: `{
  "vendor": "omada",
  "actionType": "config",
  "deviceId": "omd_sw_04",
  "config": { "patch": { "vlan": 20 } }
}`,
      responseExample: `{
  "ok": true,
  "jobId": "npj_a02f",
  "status": "pending-approval",
  "diff": { "addedLines": 1, "removedLines": 1 },
  "autoApproved": false,
  "preSnapshotArchiveId": "arc_77c1"
}`,
      sourceFile: 'routes/network-push.js, lib/network-push.js',
    },
    {
      slug: 'subnets-create',
      method: 'POST',
      path: '/api/subnets',
      summary: 'Create a subnet',
      description: 'Adds a subnet to the tenant\'s IP address management records. Utilization (used/reserved/free against the CIDR\'s capacity) is computed on every list read, not stored, so it always reflects the current IP records rather than going stale. Subnet CRUD lives directly under /api/subnets — /api/ipam/summary is the only path actually namespaced under /api/ipam.',
      auth: 'session',
      requestBodyExample: `{ "cidr": "10.20.10.0/24", "name": "Staff VLAN", "vlan": "10", "gateway": "10.20.10.1", "site": "St. Edward — Main" }`,
      responseExample: `{
  "id": "sub_6b12",
  "cidr": "10.20.10.0/24",
  "name": "Staff VLAN",
  "vlan": "10",
  "gateway": "10.20.10.1",
  "site": "St. Edward — Main",
  "createdAt": "2026-07-21T14:10:00.000Z"
}`,
      sourceFile: 'routes/ipam.js',
    },
  ],
};
